import type {
  PriceSystemBillType,
  // PriceSystemPackage,
  // PriceSystemProperty,
  // PriceSystemRegion,
} from "../../common/PriceSystem/types";
import type { StringLikeValues } from "../../common/core/types";

export enum NodeRole {
  Master = "master",
  Worker = "worker",
}

export function nodeRoleText(v: any) {
  switch (v) {
    case NodeRole.Master:
      return "Master";
    case NodeRole.Worker:
      return "Worker";
    default:
      return "";
  }
}

export enum NodeCoreComponent {
  ApiServer = "APISERVER",
  ControllerManager = "CONTROLLERMANAGER",
  Scheduler = "SCHEDULER",
  Etcd = "ETCD",
  EventEtcd = "EVENTETCD",
}

export const CoreComponentSortScoreMap = new Map<string, number>([
  [NodeCoreComponent.ApiServer, 0],
  [NodeCoreComponent.Etcd, 1],
  [NodeCoreComponent.ControllerManager, 2],
  [NodeCoreComponent.EventEtcd, 3],
  [NodeCoreComponent.Scheduler, 4],
]);

export function nodeCoreComponentToText(v: any) {
  switch (v) {
    case NodeCoreComponent.ApiServer:
      return "apiserver";
    case NodeCoreComponent.ControllerManager:
      return "controller-manager";
    case NodeCoreComponent.Scheduler:
      return "scheduler";
    case NodeCoreComponent.Etcd:
      return "etcd";
    case NodeCoreComponent.EventEtcd:
      return "event-etcd";
    default:
      return "";
  }
}

export interface Label {
  Key: string;
  Value: string;
}
export interface Taint {
  Key: string;
  Value: string;
  Effect: string;
}

export enum ContainerRuntime {
  Docker = "DOCKER",
  Containerd = "CONTAINERD",
  C_RIO = "C-RIO",
}

export function containerRuntimeText(v: any) {
  switch (v) {
    case ContainerRuntime.C_RIO:
      return "c-rio";
    case ContainerRuntime.Docker:
      return "Docker";
    case ContainerRuntime.Containerd:
      return "Containerd";
    default:
      return "";
  }
}

export type AdvancedSetting = {
  ContainerLogMaxFiles: number;
  ContainerLogMaxSize: number;
  ContainerPath: string;
  ContainerRuntime: number;
  DataDiskMountSet: null | unknown;
  DockerPath: string;
  ExtraArg: null | unknown;
  Labels: null | Label[];
  PreUserScript: string;
  Scheduable: boolean;
  Taints: null | Taint[];
  UserScript: string;
};

export enum TemplateType {
  EPC = "EPC",
  KEC = "KEC",
}

export function templateTypeText(v: any) {
  switch (v) {
    case TemplateType.EPC:
      return "裸金属服务器";
    case TemplateType.KEC:
      return "云服务器";
    default:
      return "";
  }
}

export enum ChargeType {
  Daily = "Daily",
  HourlyInstantSettlement = "HourlyInstantSettlement",
}

export function chargeTypeText(v: any) {
  switch (v) {
    case ChargeType.Daily:
      return "按日月结";
    case ChargeType.HourlyInstantSettlement:
      return "按量付费";
    default:
      return "";
  }
}

export enum LoginMode {
  Pwd = "pwd",
  SK = "sk",
  PwdAndSK = "pwd_and_sk",
}
export type LoginModeKeys = StringLikeValues<typeof LoginMode>;

export function loginModeToText(v: string) {
  switch (v) {
    case LoginMode.Pwd:
      return "密码";
    case LoginMode.SK:
      return "密钥";
    default:
      return "";
  }
}

export enum TemplateSearchMode {
  TemplateName = "NgTemplateName",
  TemplateId = "NgTemplateId",
}

export function templateSearchModeToText(v: string) {
  switch (v) {
    case TemplateSearchMode.TemplateId:
      return "启动模板ID";
    case TemplateSearchMode.TemplateName:
      return "启动模板名称";
    default:
      return "";
  }
}

export interface Project {
  AccountId: string;
  CreateTime: string;
  Krn: string;
  ProjectDesc: string;
  ProjectId: number;
  ProjectName: string;
  Status: number;
}

export interface Vpc {
  CreateTime: string;
  VpcId: string;
  VpcName: string;
  CidrBlock: string;
  IsDefault: boolean;
  ProvidedIpv6CidrBlock: boolean;
  SecondaryCidrSet: { SecondaryCidrId: string; Cidr: string; Type: string }[];
}

export interface BaseSecurityGroupEntry {
  Description: string;
  SecurityGroupEntryId: string;
  CidrBlock: string;
  /**
   * 有效值："in" | "out"
   */
  Direction: string;
  Policy: string;
  Priority: number;
}

export interface IpEntry extends BaseSecurityGroupEntry {
  Protocol: "ip";
}
export interface TcpUdpEntry extends BaseSecurityGroupEntry {
  Protocol: "tcp" | "dup";
  /**
   * TCP或UDP协议的端口规则起始端口，只有协议为TCP\UDP类型，才必填。范围1-65535。
   */
  PortRangeFrom: number;
  /**
   * TCP或UDP协议的端口规则结束端口，只有协议为TCP\UDP类型，才必填。范围1-65535。
   */
  PortRangeTo: number;
}
export interface IcmpEntry extends BaseSecurityGroupEntry {
  Protocol: "icmp";
  IcmpType: number;
  IcmpCode: number;
}
export type SecurityGroupEntry = IpEntry | TcpUdpEntry | IcmpEntry;

export interface SecurityGroup {
  CreateTime: string;
  VpcId: string;
  SecurityGroupName: string;
  SecurityGroupId: string;
  /**
   * 有效值："default" | "other"
   */
  SecurityGroupType: string;
  /**
   * 安全组规则的信息
   */
  SecurityGroupEntrySet: SecurityGroupEntry[];
}

export enum SubnetType {
  /**
   * 云服务器子网
   */
  Normal = "Normal",
  /**
   * 裸金属服务器子网
   */
  Physical = "Physical",
  /**
   * 终端子网
   */
  Reserve = "Reserve",
}

export interface Subnet {
  CreateTime: string;
  VpcId: string;
  SubnetId: string;
  /**
   * 子网的类型，终端子网（Reserve）、云服务器子网（Normal）、裸金属服务器子网（Physical）
   */
  SubnetType: SubnetType;
  SubnetName: string;
  /**
   * 子网的网络范围，例如：10.0.0.0/24
   */
  CidrBlock: string;
  /**
   * DHCP起始IP，Reserve 类型时刻缺省
   * 类型：ip address
   */
  DhcpIpForm: string;
  /**
   * DHCP结束IP，Reserve类型时可以缺省
   * 类型: ip address
   */
  DhcpIpTo: string;
  /**
   * 网关的IP，Reserve类型时可以缺省。
   * 类型: ip address
   */
  GatewayIp: string;
  /**
   * 子网的Dns1，Reserve类型时可以缺省。
   */
  Dns1: string;
  /**
   * 子网的Dns2，Reserve类型时可以缺省。
   */
  Dns2: string;
  /**
   * NetworkAcl的ID，Reserve类型时可以缺省。
   */
  NetworkAclId: string;
  /**
   * NAT的ID，Reserve类型时可以缺省。
   */
  NatId: string;
  AvailableIpNumber: string;
  AvailabilityZoneName: string;

  ProvidedIpv6CidrBlock: boolean;
  SecondaryCidrId: string;
}
export interface SnapShot {
  SnapshotId: string;
  DiskType: string; // local_snapshot | ebs_snapshot;
  DiskSize: number; // in GB
  Type: string; // data | system
}
export interface Image {
  SnapshotSet: SnapShot;
  Type: string; // 'LocalImage' | 'CommonImage';
  AvailabilityZone: string;
  Name: string;
  ImageId: string;
  CreationDate: string;
  IsPublic: string;
  Platform: string;
  ImageState: string; // queued | saving | active | deactived | killed | deleted | pending_delete
  InstanceId: string;
  IsNpe: boolean; // 是否为互联网增强镜像，true 为增强
  Progress: string;
  ImageSource: string; // import | copy | share | extend | system
  CloudInitSupport: boolean; // 镜像是否支持 CloudInit
  Ipv6Support: boolean;
  IsModifyType: boolean; // 镜像是否支持热升配
  IsCloudMarket: boolean; // 镜像是否支持镜像市场
}

export interface Key {
  KeyId: string;
  KeyName: string;
  PublicKey: string;
  CreateTime: string;
  IsChecked: boolean;
}
export enum VolumeCategoryEnum {
  System = "system",
  Data = "data",
}

export enum VolumeStatusEnum {
  Creating = "creating",
  Available = "available",
  Attaching = "attaching",
  Inuse = "in-use",
  Detaching = "detaching",
  Extending = "extending",
  Deleting = "deleting",
  Error = "error",
}

export enum VolumeTypeEnum {
  ESSD_PL1 = "ESSD_PL1",
  ESSD_PL2 = "ESSD_PL2",
  ESSD_PL3 = "ESSD_PL3",
  SSD_3_0 = "SSD3.0",
  EHDD = "EHDD",
}

export function volumeTypeText(v: string) {
  switch (v) {
    case VolumeTypeEnum.EHDD:
      return "高效云硬盘";
    case VolumeTypeEnum.SSD_3_0:
      return "SSD3.0云硬盘";
    case VolumeTypeEnum.ESSD_PL1:
      return "极速型ESSD_PL1云硬盘";
    case VolumeTypeEnum.ESSD_PL2:
      return "极速型ESSD_PL2云硬盘";
    case VolumeTypeEnum.ESSD_PL3:
      return "极速型ESSD_PL3云硬盘";
    default:
      return "";
  }
}

export interface GetVolumesParam {
  VolumeId?: string[];
  VolumeCategory?: VolumeCategoryEnum;
  VolumeStatus?: VolumeStatusEnum;
  VolumeType?: VolumeTypeEnum;
  VolumeCreateDate?: string;
}

export interface Attachment {
  InstanceId: string;
  MountPoint: string;
  DeleteWithInstance: boolean;
}

export interface HistoryAttachment {
  InstanceId: string;
  MountPoint: string;
  AttachTime: string;
  DetachTime: string;
}

export interface Volume {
  VolumeId: string;
  VolumeName: string;
  VolumeDesc: string;
  /**
   * in GB
   */
  Size: string;
  VolumeStatus: string; // VolumeStatusEnum;
  VolumeType: string; // VolumeTypeENum;
  VolumeCategory: string; // VolumeCategoryEnum;
  /**
   * 当云硬盘状态为 in-use 时，该云硬盘关联的实例ID（主机ID）
   */
  InstanceId: string;
  CreateTime: string;
  ExpireTime: string;
  AvailabilityZone: string;
  ChargeType: string;
  /**
   * 云订单状态
   * 1 正式
   * 2 试用
   */
  InstanceTradeType: number;
  ProjectId: number;
  Attachment: Attachment[];
  HistoryAttachment: HistoryAttachment[];
}

export interface AvailabilityZone {
  AzCode: string;
}

export interface SystemDiskQuota {
  /**
   * @enum {"Local_SSD"|"SSD3.0"|"EHDD"} 系统盘类型
   * - "Local_SSD" 本地 SSD 硬盘
   * - "SSD3.0" SSD 云硬盘3.0
   * - "EHDD" 高效云盘
   */
  SystemDiskType?: string;
}

export interface DataDiskQuota {
  /**
   * @enum 数据盘类型
   * - Local_SSD
   * - SSD3.0
   * - EHDD
   */
  DataDiskType?: string;
  /**
   * 数据盘最小值
   */
  DataDiskMinSize?: number;
  /**
   * 数据盘最大值
   */
  DataDiskMaxSize?: number;
  /**
   * 支持挂载 EBS 数据盘的数量
   */
  DataDiskCount?: number;
  AvailabilityZoneSet?: AvailabilityZone[];
}

export interface InstanceTypeConfig {
  AvailabilityZoneSet?: AvailabilityZone[];
  InstanceType?: string;
  InstanceFamily?: string;
  InstanceFamilyName?: string;
  NetworkInterfaceQuota?: {
    NetworkInterfaceCount?: number;
  };
  PrivateIpQuota?: {
    PrivateIpCount?: number;
  };
  SriovNetSupport?: boolean;
  GPU?: number;
  GPUspec?: string;
  CPU?: number;
  Memory?: number;
  SystemDiskQuotaSet?: SystemDiskQuota[];
  DataDiskQuotaSet?: DataDiskQuota[];
}

export type Tag = {
  Key: string;
  Value: string;
};

export type NgTemplate = {
  CreateTime: string;
  NgTemplateId: string;
  NgTemplateName: string;
  NgTemplateType: string;
  ChargeType: string;
  InstanceType: string;
  InstanceName: string;
  InstanceNameSuffix: number;
  SystemDisk: {
    DiskType: string;
    DiskSize: number;
  };
  DataDiskGb: number;
  CPU: string;
  MemoryGb: string;
  DataDisks: Array<{
    DiskType: string;
    DiskSize: number;
    DeleteWithInstance: boolean;
  }>;
  ImageId: string;
  ImageName: string;
  VpcId: string;
  VpcName: string;
  SubnetIds: Array<string>;
  SubnetNames: Array<string>;
  SubnetStrategy: number;
  SecurityGroupId: string;
  SecurityGroupName: string;
  ProjectId: string;
  ProjectName: string;
  KeyIds: string[] | null;
  AdvancedSetting: {
    DataDiskMountSet: null | {
      AutoFormatAndMount: boolean;
      FileSystem: string;
      MountTarget: string;
    };
    DockerPath: string;
    UserScript: string;
    PreUserString: string;
    ExtraArg: null | string;
    ContainerRuntime: string;
    ContainerPath: string;
    ContainerLogMaxSize: number;
    ContainerLogMaxFiles: number;
    Labels: Array<Label>;
    Taints: Array<Taint>;
    Schedulable: boolean;
  };
  EbsTags: Tag[] | null;
  InstanceTags: Tag[] | null;
};

export enum TaintEffect {
  NoSchedule = "NoSchedule",
  PreferNoSchedule = "PreferNoSchedule",
  NoExecute = "NoExecute",
}

export function taintEffectText(v: any) {
  switch (v) {
    case TaintEffect.NoSchedule:
      return v;
    case TaintEffect.PreferNoSchedule:
      return v;
    case TaintEffect.NoExecute:
      return v;
    default:
      return "";
  }
}

export interface SellOutPackage {
  Status: string;
  SellOutStatus: boolean;
  PackageCode: string;
  Spec: string;
  AvailableVmNum?: number;
}
export interface SellOutPackageData {
  ProductName: string;
  ProductCode: string;
  ProductType: number;
  AvailabilityZone: string;
  PackageSellOuts?: SellOutPackage[];
  ShouldExpand?: boolean;
  SellOutStatus: boolean;
  AllSellOutStatus?: boolean;
  Extra?: string;
}

export interface VmSellOutInfo {
  VmTypeId: number;
  SellOutStatus: true;
}

export interface SellOutVolumeDataAll {
  AvailabilityZone: string;
  SellOutStatus: true;
  VolumeTypeCode: string;
  VolumeTypeId: number;
  VmSellOuts?: null;
}

export interface SellOutVolumeDataPartialPackage {
  AvailabilityZone: string;
  SellOutStatus: false;
  VolumeTypeCode: string;
  VolumeTypeId: number;
  VmSellOuts: VmSellOutInfo[];
}

export type SellOutVolumeData = SellOutVolumeDataAll | SellOutVolumeDataPartialPackage;

export type SellOutData = SellOutPackageData | SellOutVolumeData;

export interface KvmBillType extends PriceSystemBillType {
  billTypeEnName: string;
}
export interface KvmPackage {
  packageCode: string;
  packageName: string;
  status: number;
  packageType: number;
  packageInfo: KvmProperty[];
  refRegionList: KvmRegion[];
  refBillTypeList: KvmBillType[];
}

export interface KvmProperty {
  propCode: string;
  propName?: string;
  type?: number;
  propValueType?: number;
  propValue: {
    min?: number;
    max?: number;
    step?: number;
    value: string;
    name: string;
    enName: string;
    defaultVal: boolean;
    productTypeEnName?: string;
    productTypeCode?: string;
    productTypeName?: string;
    productTypeId?: number;
    regionList?: KvmRegion[];
    packageList?: KvmPackage[];
    billTypeList?: KvmBillType[];
    propertyList?: KvmProperty[];
    systemDisk?: {};
  }[];
}

export interface KvmAz {
  azCode: string;
  azName: string;
  enName?: string;
}

export interface KvmRegion {
  regionCode: string;
  regionName: string;
  regionType: number;
  innerCode: string;
  regionEnName: string;
  azList: KvmAz[];
  areaCode: string;
  areaName: string;
  areaEnName: string;
  active: boolean;
}

export interface KvmListResponse {
  code: number;
  data: {
    productGroupType: string;
    productGroupName: string;
    productGroupId: number;
    productTypeList: {
      productTypeEnName: string;
      productTypeCode: string;
      productTypeName: string;
      productTypeId: number;
      regionList: KvmRegion[];
      packageList: KvmPackage[];
      billTypeList: KvmBillType[];
      propertyList: KvmProperty[];
      systemDisks: {};
    }[];
  };
  sellOutData: SellOutData[];
  RootSizeQuota: Record<string, number>;
}

export type QueryRegionListResponse = {
  RegionList: {
    areaCode: string;
    areaEnName: string;
    areaName: string;
    azList: { azCode: string; azName: string }[];
    innerCode: string;
    regionCode: string;
    regionEnName: string;
    regionName: string;
    regionType: number;
  }[];
};

export type TemplateListSearchType = "id" | "name";
